import React, { useState, useRef, useEffect } from 'react';

const MonthYearPicker = ({ value, onChange, isDarkMode, required }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const dropdownRef = useRef(null);

  const months = [
    { value: '01', label: 'Janvier', short: 'Jan' },
    { value: '02', label: 'Février', short: 'Fév' },
    { value: '03', label: 'Mars', short: 'Mar' },
    { value: '04', label: 'Avril', short: 'Avr' },
    { value: '05', label: 'Mai', short: 'Mai' },
    { value: '06', label: 'Juin', short: 'Jun' },
    { value: '07', label: 'Juillet', short: 'Jul' },
    { value: '08', label: 'Août', short: 'Aoû' },
    { value: '09', label: 'Septembre', short: 'Sep' },
    { value: '10', label: 'Octobre', short: 'Oct' },
    { value: '11', label: 'Novembre', short: 'Nov' },
    { value: '12', label: 'Décembre', short: 'Déc' }
  ];

  // Parse value on mount and when value changes
  useEffect(() => {
    if (value && value.length >= 10) {
      setSelectedYear(value.slice(0, 4));
      setSelectedMonth(value.slice(5, 7));
    } else {
      setSelectedYear('');
      setSelectedMonth('');
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMonthSelect = (monthValue) => {
    setSelectedMonth(monthValue);
    if (selectedYear && selectedYear.length === 4) {
      onChange(`${selectedYear}-${monthValue}-01`);
    }
  };

  const handleYearChange = (e) => {
    const year = e.target.value;
    if (year.length <= 4 && /^\d*$/.test(year)) {
      setSelectedYear(year);
      if (selectedMonth && year.length === 4) {
        onChange(`${year}-${selectedMonth}-01`);
      }
    }
  };

  const getDisplayText = () => {
    if (selectedMonth && selectedYear) {
      const monthObj = months.find(m => m.value === selectedMonth);
      return `${monthObj ? monthObj.label : selectedMonth} ${selectedYear}`;
    }
    return 'Sélectionner mois et année';
  };

  const clearSelection = () => {
    setSelectedMonth('');
    setSelectedYear('');
    onChange('');
    setIsOpen(false);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Main Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full px-3 py-2.5 border rounded-lg text-left flex justify-between items-center
          transition-all duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
          ${isDarkMode 
            ? 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700 hover:border-gray-500' 
            : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50 hover:border-gray-400'
          }
          ${isOpen ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}
        `}
      >
        <span className={`truncate ${selectedMonth && selectedYear ? '' : 'text-gray-500'}`}>
          {getDisplayText()}
        </span>
        <i className={`
          fas fa-chevron-down transition-transform duration-200 flex-shrink-0 ml-2
          ${isOpen ? 'rotate-180' : ''}
        `}></i>
      </button>

      {/* Dropdown - Fixed positioning */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)}></div>
          
          {/* Dropdown Content */}
          <div className={`
            absolute top-full left-0 right-0 mt-2 border rounded-xl shadow-2xl z-50
            max-h-96 overflow-y-auto
            ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}
          `}>
            <div className="p-4">
              {/* Year Input */}
              <div className="mb-4">
                <label className={`
                  block text-sm font-medium mb-2
                  ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}
                `}>
                  <i className="fas fa-calendar-alt mr-2 text-blue-500"></i>
                  Année
                </label>
                <input
                  type="text"
                  value={selectedYear}
                  onChange={handleYearChange}
                  placeholder="2024"
                  maxLength="4"
                  className={`
                    w-full px-3 py-2 border rounded-lg text-center font-medium
                    focus:outline-none focus:ring-2 focus:ring-blue-500
                    ${isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                    }
                  `}
                />
              </div>

              {/* Month Grid */}
              <div className="mb-4">
                <label className={`
                  block text-sm font-medium mb-2
                  ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}
                `}>
                  <i className="fas fa-calendar-day mr-2 text-blue-500"></i>
                  Mois
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {months.map((month) => (
                    <button
                      key={month.value}
                      type="button"
                      onClick={() => handleMonthSelect(month.value)}
                      className={`
                        px-2 py-2 text-xs font-medium rounded-lg transition-all duration-150
                        focus:outline-none focus:ring-2 focus:ring-blue-500
                        ${selectedMonth === month.value
                          ? 'bg-blue-500 text-white shadow-md' 
                          : isDarkMode 
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }
                      `}
                    >
                      {month.short}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={clearSelection}
                  className={`
                    flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150
                    focus:outline-none focus:ring-2 focus:ring-gray-400
                    ${isDarkMode 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                    }
                  `}
                >
                  <i className="fas fa-trash-alt mr-2"></i>
                  Effacer
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="
                    flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150
                    focus:outline-none focus:ring-2 focus:ring-blue-500
                    bg-blue-500 text-white hover:bg-blue-600 shadow-md
                  "
                >
                  <i className="fas fa-check mr-2"></i>
                  Valider
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Hidden input for form validation */}
      {required && (
        <input
          type="text"
          value={value || ''}
          onChange={() => {}}
          required
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default MonthYearPicker;